import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { BarExtensionService } from '@service/canvas/bar-extension.service';
import { ElementAttribute } from '@src/app/models/editor';
import { Channel } from 'icreate/common/models/channel';
import { BarScaleType, BarSetting, ElementSettings } from 'icreate/common/models/editor-viewer-element';
import { SystemColor } from 'icreate/common/models/system-color';

@Component({
    selector: 'app-bar-element-settings',
    templateUrl: './bar-element-settings.component.html',
    styleUrl: './bar-element-settings.component.css',
    standalone: false,
})
export class BarElementSettingsComponent implements OnChanges {
    @Input() elementSettings: ElementSettings;
    @Input() element: SVGElement;
    @Input() assignedChannel: Channel;
    @Input() systemColors: SystemColor[];
    readonly ScaleTypeOptions = [
        { value: BarScaleType.SINGLE, text: 'mimic.settings.bar.scale.single' },
        { value: BarScaleType.ZERO, text: 'mimic.settings.bar.scale.zero' },
    ];

    readonly IntervalsOptions = [
        { value: 0, text: 0 },
        { value: 1, text: 1 },
        { value: 2, text: 2 },
    ];

    readonly UnitDisplayOptions = [
        { value: true, text: 'dlg.enabled' },
        { value: false, text: 'dlg.disabled' },
    ];

    showingScaleType: boolean = false;
    scaleType: BarScaleType = BarScaleType.SINGLE;

    showingIntervals: boolean = false;
    intervalOption: 0 | 1 | 2 = 0;

    displayUnits: boolean = true;
    displayValue: boolean = true;
    displayName: boolean = false;

    private initialElement: any;

    constructor(private barExtension: BarExtensionService) {}

    ngOnChanges(changes: SimpleChanges): void {
        if (this.element) {
            this.initialElement = this.element.cloneNode(true);

            const labelsContainer = this.element.querySelector('[labelsContainer="true"]') as HTMLElement;
            const valueContainer = this.element.querySelector('[valueContainer="true"]') as HTMLElement;
            const channelNameContainer = this.element.querySelector('[channelNameContainer="true"]') as HTMLElement;
            const linearGaugeRoot = this.element.querySelector('[linearGaugeRoot="true"]') as HTMLElement;

            this.scaleType = linearGaugeRoot.getAttribute(ElementAttribute.barScaleType) as BarScaleType;
            this.intervalOption = Number(linearGaugeRoot.getAttribute(ElementAttribute.barIntervals)) as 0 | 1 | 2;

            this.displayUnits = labelsContainer.style.display == 'flex';
            this.displayValue = valueContainer.style.display == 'flex';
            this.displayName = channelNameContainer.style.display == 'flex';
        }
    }

    handleScaleChange() {
        const barSetting = this.currentBarSetting;
        this.barExtension.updateDisplay(this.element, this.assignedChannel, barSetting, this.systemColors);
    }

    handleIntervalChange() {
        const barSetting = this.currentBarSetting;
        this.barExtension.updateDisplay(this.element, this.assignedChannel, barSetting, this.systemColors);
    }

    handleUnitDisplayChange(event) {
        this.displayUnits = event.checked;
        this.barExtension.setUnitsVisible(this.element, this.displayUnits);
    }

    handleValueDisplayChange(event) {
        this.displayValue = event.checked;
        this.barExtension.setValueVisible(this.element, this.displayValue);
    }

    handleNameDisplayChange(event) {
        this.displayName = event.checked;
        this.barExtension.setNameVisible(this.element, this.displayName);
    }

    updateInitialElement(newElement) {
        this.initialElement = newElement.cloneNode(true);
    }

    get hasChanges(): boolean {
        if (!this.initialElement || !this.element) return false;
        const labelsContainer = this.initialElement.querySelector('[labelsContainer="true"]') as HTMLElement;
        const valueContainer = this.initialElement.querySelector('[valueContainer="true"]') as HTMLElement;
        const channelNameContainer = this.initialElement.querySelector('[channelNameContainer="true"]') as HTMLElement;
        const linearGaugeRoot = this.initialElement.querySelector('[linearGaugeRoot="true"]') as HTMLElement;

        const initialScaleType = linearGaugeRoot.getAttribute(ElementAttribute.barScaleType) as BarScaleType;
        const initialIntervalOption = Number(linearGaugeRoot.getAttribute(ElementAttribute.barIntervals)) as 0 | 1 | 2;

        const initiaDisplayUnits = labelsContainer.style.display == 'flex';
        const initiaDisplayValue = valueContainer.style.display == 'flex';
        const initiaDisplayName = channelNameContainer.style.display == 'flex';

        return (
            initialScaleType !== this.scaleType ||
            initialIntervalOption !== this.intervalOption ||
            initiaDisplayUnits !== this.displayUnits ||
            initiaDisplayValue !== this.displayValue ||
            initiaDisplayName !== this.displayName
        );
    }

    get currentBarSetting(): BarSetting {
        return {
            interval: this.intervalOption,
            scale: this.scaleType,
        };
    }
}
